/**
 * useLiveSessionRequest - Live session bootstrap and voice reconnect helpers
 *
 * Extracted from App.tsx to isolate the logic that starts live sessions and
 * voice calls, which will be shared by the hosted shell and future native live
 * embeds.
 */

import { MutableRefObject, useCallback, useEffect, useRef } from 'react';

import type { MakeCallOptions, VoiceCallSessionStateDetail } from '../../channels/voice/voiceCallFeature';
import type { AvatarSessionContext, AvatarSessionStatus } from '../../session';
import { buildLiveSipHeaders } from '../../session/liveMode';
import { resolveWithVoiceCall } from '../utils/liveControllerUtils';

export type RequestLiveSessionFn = (
  overrides?: Partial<AvatarSessionContext>,
  withVoiceCall?: boolean
) => AvatarSessionContext;

interface LiveSessionRequestRefs {
  requestLiveSessionRef: MutableRefObject<RequestLiveSessionFn | null>;
  liveManualHangupRef: MutableRefObject<boolean>;
  liveVoicePromptStartedRef: MutableRefObject<boolean>;
  liveVoiceRetryTimerRef: MutableRefObject<number | null>;
  liveVoiceRetryCountRef: MutableRefObject<number>;
  liveVoiceAttemptRef: MutableRefObject<number>;
  livePromptListeningRef: MutableRefObject<boolean>;
  liveSpeakingRef: MutableRefObject<boolean>;
  liveThinkingRef: MutableRefObject<boolean>;
  liveAvatarModelIdRef: MutableRefObject<string | null>;
}

interface UseLiveSessionRequestOptions {
  refs: LiveSessionRequestRefs;
  ensureLiveContext: (overrides?: Partial<AvatarSessionContext>) => AvatarSessionContext;
  clearLiveVoiceRetryTimer: () => void;
  clearLivePlaybackCountdown: () => void;
  clearLiveSpeakingTimeout: () => void;
  stopLiveSyntheticAudioSync: () => void;
  dispatchAvatarEvent: (eventName: string, detail: unknown) => void;
  initiateCall: (target: string, options?: MakeCallOptions) => boolean;
  avatarLoadingMessage?: string | null;
  liveModeActive: boolean;
  isAvatarReady: boolean;
  isVoiceInitialized: boolean;
  liveDestination?: string | null;
  liveVoiceId?: string | null;
  isVisualOnly?: boolean;
  closeBrowserAccessPrompt: () => void;
  setIsCallActive: (value: boolean) => void;
  setIsWebrtcCall: (value: boolean) => void;
  setIsInVoiceCall: (value: boolean) => void;
  setLiveConnectRequested: (value: boolean) => void;
  setLiveConnectionError: (value: string | null) => void;
  setDisplayText: (value: string) => void;
  setLiveAssistantText: (value: string) => void;
  setLiveUserTranscript: (value: string) => void;
  setLiveUserTranscriptFinal: (value: boolean) => void;
  setLiveStatus: (
    status: AvatarSessionStatus,
    context?: AvatarSessionContext | null,
    lastError?: string
  ) => void;
  showToast: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
}

export interface UseLiveSessionRequestReturn {
  requestLiveSession: RequestLiveSessionFn;
  pendingVoiceInitRef: MutableRefObject<boolean>;
}

function getDetailReason(detail: VoiceCallSessionStateDetail | undefined, fallback: string): string {
  if (typeof detail?.reason === 'string' && detail.reason.trim()) {
    return detail.reason;
  }

  if (typeof detail?.cause === 'string' && detail.cause.trim()) {
    return detail.cause;
  }

  return fallback;
}

export function useLiveSessionRequest({
  refs,
  ensureLiveContext,
  clearLiveVoiceRetryTimer,
  clearLivePlaybackCountdown,
  clearLiveSpeakingTimeout,
  stopLiveSyntheticAudioSync,
  dispatchAvatarEvent,
  initiateCall,
  avatarLoadingMessage,
  liveModeActive,
  isAvatarReady,
  isVoiceInitialized,
  liveDestination,
  liveVoiceId,
  isVisualOnly,
  closeBrowserAccessPrompt,
  setIsCallActive,
  setIsWebrtcCall,
  setIsInVoiceCall,
  setLiveConnectRequested,
  setLiveConnectionError,
  setDisplayText,
  setLiveAssistantText,
  setLiveUserTranscript,
  setLiveUserTranscriptFinal,
  setLiveStatus,
  showToast
}: UseLiveSessionRequestOptions): UseLiveSessionRequestReturn {
  const {
    requestLiveSessionRef,
    liveManualHangupRef,
    liveVoicePromptStartedRef,
    liveVoiceRetryTimerRef,
    liveVoiceRetryCountRef,
    liveVoiceAttemptRef,
    livePromptListeningRef,
    liveSpeakingRef,
    liveThinkingRef,
    liveAvatarModelIdRef
  } = refs;

  const queueLiveVoiceReconnect = useCallback((context: AvatarSessionContext, reason?: string) => {
    if (!liveModeActive || liveManualHangupRef.current) {
      return;
    }

    if (liveVoiceRetryTimerRef.current) {
      return;
    }

    const nextAttempt = liveVoiceRetryCountRef.current + 1;
    const reconnectMessage = nextAttempt === 1
      ? 'Reconnecting live session...'
      : 'Reconnecting live session via relay...';

    livePromptListeningRef.current = false;
    liveSpeakingRef.current = false;
    liveThinkingRef.current = false;
    clearLivePlaybackCountdown();
    clearLiveSpeakingTimeout();
    stopLiveSyntheticAudioSync();
    dispatchAvatarEvent('avatar:listening', { listening: false });
    dispatchAvatarEvent('avatar:thinking', { thinking: false });
    setIsCallActive(false);
    setIsWebrtcCall(false);
    setIsInVoiceCall(false);

    if (nextAttempt > 2) {
      const failureMessage = reason || 'Live voice connection was lost. Press Listen to try again.';
      clearLiveVoiceRetryTimer();
      liveVoiceRetryCountRef.current = 0;
      setLiveConnectRequested(false);
      setLiveConnectionError(failureMessage);
      setDisplayText(failureMessage);
      setLiveAssistantText(failureMessage);
      setLiveStatus('error', context, failureMessage);
      return;
    }

    liveVoiceRetryCountRef.current = nextAttempt;
    setLiveConnectRequested(true);
    setLiveConnectionError(null);
    setDisplayText(reconnectMessage);
    setLiveAssistantText(reconnectMessage);
    setLiveStatus('connecting', context, reason);
    liveVoiceRetryTimerRef.current = window.setTimeout(() => {
      liveVoiceRetryTimerRef.current = null;
      requestLiveSessionRef.current?.(context, true);
    }, nextAttempt === 1 ? 1200 : 1800);
  }, [
    clearLivePlaybackCountdown,
    clearLiveSpeakingTimeout,
    clearLiveVoiceRetryTimer,
    dispatchAvatarEvent,
    liveManualHangupRef,
    liveModeActive,
    livePromptListeningRef,
    liveSpeakingRef,
    liveThinkingRef,
    liveVoiceRetryCountRef,
    liveVoiceRetryTimerRef,
    requestLiveSessionRef,
    setDisplayText,
    setIsCallActive,
    setIsInVoiceCall,
    setIsWebrtcCall,
    setLiveAssistantText,
    setLiveConnectRequested,
    setLiveConnectionError,
    setLiveStatus,
    stopLiveSyntheticAudioSync
  ]);

  const pendingVoiceInitRef = useRef(false);
  // Pending live-session request that fired while the avatar 3D scene was
  // still loading. Without this, hosts (AgentLink, Decision Memory, builder
  // embeds) would have to ask the user to click "Listen" a second time after
  // the scene finishes loading — which is exactly the symptom reported in
  // the AgentLink Apr-29 session ("first click sits there, then I press Stop
  // Listening + Listen again and it works"). We capture the latest request
  // here and the `isAvatarReady` watcher below auto-fires it once the scene
  // is ready.
  const pendingAvatarReadyRequestRef = useRef<{
    overrides: Partial<AvatarSessionContext>;
    withVoiceCall: boolean;
  } | null>(null);

  // Always-current readiness mirrors. `requestLiveSession` is frequently
  // invoked through a closure captured BEFORE an async gap (Decision Memory
  // Aug-12: the call click awaits the browser audio-unlock for ~1s before
  // requesting the session). If the avatar became ready inside that gap, the
  // stale closure's `isAvatarReady === false` parked the request on
  // `pendingAvatarReadyRequestRef` AFTER the isAvatarReady watcher had
  // already fired — and since isAvatarReady never flips again, the parked
  // request was stranded forever and the call sat on "connecting". Reading
  // these refs (updated every render) instead of the closure values makes a
  // mis-park impossible; a legitimate park still gets retried by the
  // watchers when the state genuinely flips later. The same race applies to
  // `isVoiceInitialized` (UA registration can land inside the same gap).
  const isAvatarReadyRef = useRef(isAvatarReady);
  isAvatarReadyRef.current = isAvatarReady;
  const isVoiceInitializedRef = useRef(isVoiceInitialized);
  isVoiceInitializedRef.current = isVoiceInitialized;

  const requestLiveSession = useCallback<RequestLiveSessionFn>((overrides = {}, withVoiceCall) => {
    // See `resolveWithVoiceCall` for the full contract + rationale.
    // Pure helper so the SIP-vs-browser-only decision is unit-testable
    // without React.
    const effectiveWithVoiceCall = resolveWithVoiceCall({
      callerOverride: withVoiceCall,
      isVisualOnly,
      liveDestination
    });
    const nextContext = ensureLiveContext(overrides);
    setLiveUserTranscript('');
    setLiveUserTranscriptFinal(false);

    if (effectiveWithVoiceCall) {
      liveManualHangupRef.current = false;
      liveVoicePromptStartedRef.current = false;
      clearLiveVoiceRetryTimer();
      setLiveAssistantText('Connecting to your call...');
      if (liveModeActive && !isAvatarReadyRef.current) {
        const message = avatarLoadingMessage || 'Please wait for the avatar to finish loading';
        // Defer the request — the watcher below will retry as soon as
        // isAvatarReady flips true. For SIP-backed hosts the visual websocket
        // is gated until the call is accepted, so this cannot race the runtime
        // session bootstrap.
        pendingAvatarReadyRequestRef.current = { overrides, withVoiceCall };
        setLiveConnectRequested(true);
        setLiveConnectionError(null);
        setLiveStatus('connecting', nextContext, message);
        setDisplayText(message);
        setLiveAssistantText(message);
        showToast(message, 'info');
        return nextContext;
      }

      if (!isVoiceInitializedRef.current) {
        pendingVoiceInitRef.current = true;
        setLiveConnectRequested(true);
        setLiveConnectionError(null);
        setLiveStatus('connecting', nextContext, 'Connecting voice...');
        setDisplayText('Connecting voice...');
        setLiveAssistantText('Connecting voice...');
        return nextContext;
      }

      if (!liveDestination) {
        const message = 'Live mode voice destination is not configured';
        setLiveConnectRequested(false);
        setLiveConnectionError(message);
        setLiveStatus('error', nextContext, message);
        setLiveAssistantText(message);
        showToast(message, 'warning');
        return nextContext;
      }

      setLiveConnectRequested(true);
      setLiveConnectionError(null);
      setLiveStatus('connecting', nextContext);
      const voiceAttempt = liveVoiceAttemptRef.current + 1;
      liveVoiceAttemptRef.current = voiceAttempt;
      // Retry strategy: on the second attempt force TURN-relay only, since
      // direct/host candidates were unable to establish a working media path
      // the first time. This helps in production where corporate firewalls
      // block UDP. BUT: in local dev there's typically no reachable TURN
      // server, so a relay-only offer ends up with no candidates at all and
      // Asterisk responds with SIP 488 Bad Media Description (an empty SDP).
      // Detect that case and stay with 'all' so the retry has a chance to
      // succeed against a local Asterisk instance.
      const isLocalSipDest = typeof window !== 'undefined'
        && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
      const iceTransportPolicy: RTCIceTransportPolicy = (liveVoiceRetryCountRef.current > 0 && !isLocalSipDest)
        ? 'relay'
        : 'all'
      const started = initiateCall(liveDestination, {
        extraHeaders: buildLiveSipHeaders(nextContext, {
          avatarModelId: liveAvatarModelIdRef.current ?? undefined,
          voiceId: liveVoiceId ?? undefined
        }),
        iceTransportPolicy,
        onSessionStateChange: (state, detail) => {
          console.log(`[LiveMode] voice session state change ${JSON.stringify({
            state,
            detail: detail ?? null,
            retryCount: liveVoiceRetryCountRef.current,
            manualHangup: liveManualHangupRef.current,
            promptStarted: liveVoicePromptStartedRef.current
          })}`);

          if (liveVoiceAttemptRef.current !== voiceAttempt) {
            return;
          }

          if (state === 'accepted' || state === 'confirmed' || state === 'audio-ready') {
            clearLiveVoiceRetryTimer();
            setLiveConnectionError(null);
            setIsWebrtcCall(true);
            setIsCallActive(true);
            if (state === 'audio-ready') {
              closeBrowserAccessPrompt();
            }
            return;
          }

          if ((state === 'connection-lost' || state === 'failed') && !liveVoicePromptStartedRef.current) {
            queueLiveVoiceReconnect(
              nextContext,
              getDetailReason(detail, 'Live voice connection lost')
            );
            return;
          }

          if (state === 'ended' && !liveManualHangupRef.current && !liveVoicePromptStartedRef.current) {
            queueLiveVoiceReconnect(
              nextContext,
              getDetailReason(detail, 'Live voice call ended before the session became ready')
            );
          }
        }
      });
      if (started) {
        setLiveStatus('connecting', nextContext, 'Connecting voice...');
      } else {
        setLiveConnectRequested(false);
        setLiveStatus('error', nextContext, 'Failed to start live voice call');
        setLiveAssistantText('Failed to start live voice call');
      }
    } else {
      setLiveConnectRequested(true);
      setLiveConnectionError(null);
      setLiveStatus('connecting', nextContext);
      setLiveAssistantText('Connecting to live session...');
    }

    return nextContext;
  }, [
    avatarLoadingMessage,
    clearLiveVoiceRetryTimer,
    closeBrowserAccessPrompt,
    ensureLiveContext,
    initiateCall,
    isAvatarReady,
    isVisualOnly,
    isVoiceInitialized,
    liveAvatarModelIdRef,
    liveDestination,
    liveVoiceId,
    liveManualHangupRef,
    liveModeActive,
    liveVoiceAttemptRef,
    liveVoicePromptStartedRef,
    liveVoiceRetryCountRef,
    queueLiveVoiceReconnect,
    setDisplayText,
    setIsCallActive,
    setIsWebrtcCall,
    setLiveAssistantText,
    setLiveConnectRequested,
    setLiveConnectionError,
    setLiveStatus,
    setLiveUserTranscript,
    setLiveUserTranscriptFinal,
    showToast
  ]);

  useEffect(() => {
    requestLiveSessionRef.current = requestLiveSession;
  }, [requestLiveSession, requestLiveSessionRef]);

  useEffect(() => {
    if (
      !pendingVoiceInitRef.current ||
      !isVoiceInitialized ||
      !liveModeActive ||
      isVisualOnly ||
      liveManualHangupRef.current
    ) {
      return;
    }

    pendingVoiceInitRef.current = false;
    requestLiveSessionRef.current?.(undefined, true);
  }, [isVoiceInitialized, liveModeActive, isVisualOnly, liveManualHangupRef, requestLiveSessionRef]);

  // Slice B fix (AgentLink Apr-29 session): if the user clicked "Listen"
  // before the avatar 3D scene finished loading, retry the deferred request
  // automatically as soon as isAvatarReady flips true. Without this the
  // user has to click Listen twice on a cold start (first click sits idle
  // until they manually click Stop + Listen again).
  useEffect(() => {
    if (
      !isAvatarReady ||
      !liveModeActive ||
      liveManualHangupRef.current ||
      !pendingAvatarReadyRequestRef.current
    ) {
      return;
    }

    const pending = pendingAvatarReadyRequestRef.current;
    pendingAvatarReadyRequestRef.current = null;
    requestLiveSessionRef.current?.(pending.overrides, pending.withVoiceCall);
  }, [isAvatarReady, liveModeActive, liveManualHangupRef, requestLiveSessionRef]);

  return {
    requestLiveSession,
    pendingVoiceInitRef
  };
}

export default useLiveSessionRequest;
